'use client';

import { Headset } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { cn } from '@/lib/utils';

type IntercomFn = (command: string, ...args: unknown[]) => void;

/**
 * Opens the Intercom support messenger.
 *
 * Gated behind the `messenger` feature flag — when it is off the Intercom SDK
 * is never loaded, so the button renders nothing. Mirrors the "Contact Support"
 * action in the main app sidebar; used in the in-matter icon rail in place of
 * the retired "Speak with the Vale team" dialog.
 */
export function IntercomSupportButton({
  compact = false
}: {
  compact?: boolean;
}) {
  const { hasFeature } = useFeatureFlags();

  if (!hasFeature('messenger')) return null;

  const openMessenger = () => {
    const intercom = (window as { Intercom?: IntercomFn }).Intercom;
    if (typeof intercom === 'function') intercom('show');
  };

  const button = (
    <Button
      variant="outline"
      size={compact ? 'icon' : 'default'}
      className={cn(
        'hover:bg-accent hover:text-accent-foreground gap-2',
        compact ? 'h-8 w-8' : 'w-full justify-center px-3 text-sm font-normal'
      )}
      onClick={openMessenger}
    >
      <Headset className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!compact && 'Contact Support'}
    </Button>
  );

  return compact ? (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">Contact Support</TooltipContent>
    </Tooltip>
  ) : (
    button
  );
}
